(() => {
  'use strict';

  const preferenceKey = 'vna:language-popup:v1';
  const popupSelector = '[data-vna-language-popup]';
  const isLanguage = (value) => value === 'en' || value === 'ja';

  const readPreference = () => {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const storage = window[name];
        const value = JSON.parse(storage.getItem(preferenceKey) || 'null');
        if (value && value.handled === true) return value;
      } catch (error) {
        // Private browsing and blocked storage must not break localization.
      }
    }
    return null;
  };

  const savePreference = (language, source) => {
    const value = JSON.stringify({
      handled: true,
      language: isLanguage(language) ? language : null,
      source,
      selectedAt: isLanguage(language) ? Date.now() : null,
      handledAt: Date.now()
    });

    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name].setItem(preferenceKey, value); } catch (error) { /* Storage is optional. */ }
    }
  };

  // Keep all existing Shopify language switchers. Record their form submissions,
  // without changing country_code or intercepting the native localization form.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('input[name="form_type"][value="localization"]')) return;

    const submitter = event.submitter || document.activeElement;
    const language = submitter && submitter.name === 'language_code' ? submitter.value : '';
    if (isLanguage(language)) savePreference(language, form.matches('#VnaLanguagePopupForm') ? 'popup' : 'site-menu');
  }, true);

  // The legacy header's custom element calls form.submit(), which does not fire
  // a submit event. Its selected link is therefore tracked at click time.
  document.addEventListener('click', (event) => {
    const option = event.target.closest('localization-form a[data-value]');
    if (option && isLanguage(option.dataset.value)) savePreference(option.dataset.value, 'site-menu');
  }, true);

  const init = () => {
    const popup = document.querySelector(popupSelector);
    const state = window.VnaLocalizationState;
    if (!popup || !state || !state.ready || readPreference()) return;

    const dialog = popup.querySelector('.vna-language-popup__dialog');
    const closeButton = popup.querySelector('[data-vna-language-popup-close]');
    if (!dialog || !closeButton) return;

    let previousFocus = null;
    let previousHtmlOverflow = '';
    let previousBodyOverflow = '';

    const close = () => {
      if (popup.hidden) return;
      popup.hidden = true;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overflow = previousBodyOverflow;
      savePreference(null, 'dismissed');
      if (previousFocus && previousFocus.isConnected) previousFocus.focus();
    };

    const open = () => {
      if (!popup.hidden || readPreference()) return;
      previousFocus = document.activeElement;
      previousHtmlOverflow = document.documentElement.style.overflow;
      previousBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      popup.hidden = false;
      popup.querySelector('.vna-language-popup__choice--english')?.focus();
    };

    closeButton.addEventListener('click', close);
    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.querySelectorAll('button:not([disabled])'));
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const otherDialogVisible = () => Array.from(document.querySelectorAll('[role="dialog"], .halo-popup'))
      .some((element) => {
        if (popup.contains(element) || element.closest('[hidden], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0 && element.getClientRects().length > 0;
      });

    const waitForClearPage = (attempt = 0) => {
      if (readPreference() || state.redirecting) return;
      if (otherDialogVisible()) {
        if (attempt < 40) window.setTimeout(() => waitForClearPage(attempt + 1), 250);
        return;
      }
      open();
    };

    Promise.resolve(state.ready).then((result) => {
      if (!result || !result.country || result.country === 'JP' || state.redirecting || readPreference()) return;
      if (/^\/(?:en\/|ja\/)?(?:cart|checkouts?)(?:\/|$)/i.test(window.location.pathname)) return;
      const show = () => window.setTimeout(waitForClearPage, 250);
      if (document.readyState === 'complete') show();
      else window.addEventListener('load', show, { once: true });
    }).catch(() => { /* If IP lookup fails, do not guess the visitor's country. */ });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
